import React, { useState, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { connect } from 'react-redux';
import { createServiceableArea, getServiceableArea, updateServiceableArea, clearServiceableArea } from '../../actions/serviceableArea';
import { ArrowLeft, Save, MapPin, Map, Activity, FileText } from 'lucide-react';

const initialState = {
    name: '',
    description: '',
    city: '',
    state: '',
    isActive: true,
    minNoticeHours: 1,
    maxDeliveriesPerHour: 10,
    boundary: {
        type: 'Polygon',
        coordinates: [[]]
    }
};

const ServiceableArea = ({
    createServiceableArea,
    getServiceableArea,
    updateServiceableArea,
    clearServiceableArea,
    serviceableArea,
    loading
}) => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [formData, setFormData] = useState(initialState);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState({});
    const [coordinates, setCoordinates] = useState('');

    const isNewArea = id === 'new';

    useEffect(() => {
        document.title = isNewArea ? 'Create Serviceable Area' : 'Edit Serviceable Area';
        
        if (!isNewArea && (!serviceableArea || serviceableArea._id !== id)) {
            getServiceableArea(id);
        } else if (!isNewArea && serviceableArea && serviceableArea._id === id) {
            setFormData({ ...initialState, ...serviceableArea });
            
            // Convert coordinates to string format for editing
            if (serviceableArea.boundary?.coordinates?.[0]) {
                const coordsString = serviceableArea.boundary.coordinates[0]
                    .map(coord => `${coord[0]},${coord[1]}`)
                    .join(' ');
                setCoordinates(coordsString);
            }
        } else if (isNewArea) {
            clearServiceableArea();
            setFormData(initialState);
            setCoordinates('');
        }
    }, [id, isNewArea, serviceableArea, getServiceableArea, clearServiceableArea]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }

        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleCoordinatesChange = (e) => {
        const value = e.target.value;
        setCoordinates(value);
        
        if (errors.coordinates) {
            setErrors(prev => ({ ...prev, coordinates: '' }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.name.trim()) {
            newErrors.name = 'Name is required';
        }

        if (!formData.city.trim()) {
            newErrors.city = 'City is required';
        }

        if (!formData.state.trim()) {
            newErrors.state = 'State is required';
        }

        const minNotice = Number(formData.minNoticeHours);
        if (!Number.isFinite(minNotice) || minNotice < 1) {
            newErrors.minNoticeHours = 'Minimum notice must be at least 1 hour';
        }

        const maxPerHour = Number(formData.maxDeliveriesPerHour);
        if (!Number.isInteger(maxPerHour) || maxPerHour < 1) {
            newErrors.maxDeliveriesPerHour = 'Max deliveries per hour must be a whole number of at least 1';
        }

        if (!coordinates.trim()) {
            newErrors.coordinates = 'Boundary coordinates are required';
        } else {
            // Validate coordinate format
            const coordPairs = coordinates.trim().split(' ');
            if (coordPairs.length < 3) {
                newErrors.coordinates = 'At least 3 coordinate pairs are required for a polygon';
            } else {
                const validCoords = coordPairs.every(pair => {
                    const [lng, lat] = pair.split(',');
                    return !isNaN(parseFloat(lng)) && !isNaN(parseFloat(lat));
                });
                
                if (!validCoords) {
                    newErrors.coordinates = 'Invalid coordinate format. Use: lng,lat lng,lat lng,lat...';
                }
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const parseCoordinates = () => {
        const coordPairs = coordinates.trim().split(' ');
        const parsedCoords = coordPairs.map(pair => {
            const [lng, lat] = pair.split(',');
            return [parseFloat(lng), parseFloat(lat)];
        });
        
        // Ensure the polygon is closed (first and last coordinates are the same)
        if (parsedCoords.length > 0) {
            const first = parsedCoords[0];
            const last = parsedCoords[parsedCoords.length - 1];
            if (first[0] !== last[0] || first[1] !== last[1]) {
                parsedCoords.push([...first]);
            }
        }
        
        return [parsedCoords];
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }

        try {
            setIsSubmitting(true);
            
            const submissionData = {
                ...formData,
                minNoticeHours: Number(formData.minNoticeHours),
                maxDeliveriesPerHour: Number(formData.maxDeliveriesPerHour),
                boundary: {
                    type: 'Polygon',
                    coordinates: parseCoordinates()
                }
            };
            
            if (isNewArea) {
                await createServiceableArea(submissionData);
                navigate('/inventory/v1.2/serviceable-areas');
            } else {
                await updateServiceableArea(serviceableArea._id, submissionData);
            }
        } catch (error) {
            console.error('Error submitting form:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loading && !isNewArea) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <div className="text-center">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3 text-muted">Loading serviceable area details...</p>
                </div>
            </div>
        );
    }

    return (
        <Fragment>
            <div className="container-fluid py-4">
                {/* Header */}
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center">
                            <Link
                                className="btn btn-outline-primary d-flex align-items-center"
                                to="/inventory/v1.2/serviceable-areas"
                            >
                                <ArrowLeft size={16} className="me-2" />
                                Back to Serviceable Areas
                            </Link>
                            <h2 className="text-primary mb-0 fw-bold">
                                {isNewArea ? 'Create Serviceable Area' : `Edit ${formData.name}`}
                            </h2>
                            <div></div>
                        </div>
                    </div>
                </div>

                <form onSubmit={onSubmit}>
                    <div className="row">
                        {/* Basic Information */}
                        <div className="col-lg-8">
                            <div className="card shadow-sm mb-4">
                                <div className="card-header bg-primary text-white">
                                    <h5 className="mb-0 d-flex align-items-center">
                                        <FileText size={20} className="me-2" />
                                        Basic Information
                                    </h5>
                                </div>
                                <div className="card-body">
                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-bold">Area Name *</label>
                                            <input
                                                type="text"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleChange}
                                                className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                                                placeholder="Enter serviceable area name"
                                            />
                                            {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                                        </div>

                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-bold">Status</label>
                                            <div className="form-check form-switch">
                                                <input
                                                    type="checkbox"
                                                    name="isActive"
                                                    checked={formData.isActive}
                                                    onChange={handleChange}
                                                    className="form-check-input"
                                                    id="isActive"
                                                />
                                                <label className="form-check-label" htmlFor="isActive">
                                                    <Activity size={16} className="me-1" />
                                                    {formData.isActive ? 'Active' : 'Inactive'}
                                                </label>
                                            </div>
                                        </div>

                                        <div className="col-12 mb-3">
                                            <label className="form-label fw-bold">Description</label>
                                            <textarea
                                                name="description"
                                                value={formData.description}
                                                onChange={handleChange}
                                                className="form-control"
                                                placeholder="Enter area description (optional)"
                                                rows="3"
                                            />
                                        </div>

                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-bold">Minimum Notice (hours) *</label>
                                            <input
                                                type="number"
                                                name="minNoticeHours"
                                                min="1"
                                                step="0.5"
                                                value={formData.minNoticeHours}
                                                onChange={handleChange}
                                                className={`form-control ${errors.minNoticeHours ? 'is-invalid' : ''}`}
                                            />
                                            {errors.minNoticeHours && <div className="invalid-feedback">{errors.minNoticeHours}</div>}
                                            <div className="form-text">Customers cannot schedule a delivery sooner than this from now.</div>
                                        </div>

                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-bold">Max Deliveries Per Hour *</label>
                                            <input
                                                type="number"
                                                name="maxDeliveriesPerHour"
                                                min="1"
                                                step="1"
                                                value={formData.maxDeliveriesPerHour}
                                                onChange={handleChange}
                                                className={`form-control ${errors.maxDeliveriesPerHour ? 'is-invalid' : ''}`}
                                            />
                                            {errors.maxDeliveriesPerHour && <div className="invalid-feedback">{errors.maxDeliveriesPerHour}</div>}
                                            <div className="form-text">Scheduled deliveries accepted per clock hour (IST) in this area.</div>
                                        </div>

                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-bold">City *</label>
                                            <div className="input-group">
                                                <span className="input-group-text">
                                                    <MapPin size={16} />
                                                </span>
                                                <input
                                                    type="text"
                                                    name="city"
                                                    value={formData.city}
                                                    onChange={handleChange}
                                                    className={`form-control ${errors.city ? 'is-invalid' : ''}`}
                                                    placeholder="Enter city name"
                                                />
                                                {errors.city && <div className="invalid-feedback">{errors.city}</div>}
                                            </div>
                                        </div>

                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-bold">State *</label>
                                            <div className="input-group">
                                                <span className="input-group-text">
                                                    <Map size={16} />
                                                </span>
                                                <input
                                                    type="text"
                                                    name="state"
                                                    value={formData.state}
                                                    onChange={handleChange}
                                                    className={`form-control ${errors.state ? 'is-invalid' : ''}`}
                                                    placeholder="Enter state name"
                                                />
                                                {errors.state && <div className="invalid-feedback">{errors.state}</div>}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Boundary Coordinates */}
                            <div className="card shadow-sm mb-4">
                                <div className="card-header bg-info text-white">
                                    <h5 className="mb-0 d-flex align-items-center">
                                        <Map size={20} className="me-2" />
                                        Boundary Coordinates
                                    </h5>
                                </div>
                                <div className="card-body">
                                    <div className="alert alert-info">
                                        <strong>Coordinate Format:</strong> Enter coordinates as space-separated pairs of longitude,latitude.
                                        <br />
                                        <strong>Example:</strong> 77.5946,12.9716 77.6146,12.9716 77.6146,12.9916 77.5946,12.9916
                                    </div>
                                    
                                    <div className="mb-3">
                                        <label className="form-label fw-bold">Polygon Coordinates *</label>
                                        <textarea
                                            value={coordinates}
                                            onChange={handleCoordinatesChange}
                                            className={`form-control ${errors.coordinates ? 'is-invalid' : ''}`}
                                            placeholder="Enter coordinates as: lng,lat lng,lat lng,lat..."
                                            rows="4"
                                            style={{ fontFamily: 'monospace' }}
                                        />
                                        {errors.coordinates && <div className="invalid-feedback">{errors.coordinates}</div>}
                                        <div className="form-text">
                                            Minimum 3 coordinate pairs required. The polygon will be automatically closed.
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Preview/Info Panel */}
                        <div className="col-lg-4">
                            <div className="card shadow-sm">
                                <div className="card-header bg-light">
                                    <h5 className="mb-0">Area Summary</h5>
                                </div>
                                <div className="card-body">
                                    <div className="mb-3">
                                        <strong>Name:</strong>
                                        <div className="text-muted">
                                            {formData.name || <em>Not specified</em>}
                                        </div>
                                    </div>
                                    
                                    <div className="mb-3">
                                        <strong>Location:</strong>
                                        <div className="text-muted">
                                            {formData.city && formData.state 
                                                ? `${formData.city}, ${formData.state}`
                                                : <em>Not specified</em>
                                            }
                                        </div>
                                    </div>
                                    
                                    <div className="mb-3">
                                        <strong>Scheduling:</strong>
                                        <div className="text-muted">
                                            {formData.minNoticeHours}h min notice, {formData.maxDeliveriesPerHour} deliveries/hour
                                        </div>
                                    </div>

                                    <div className="mb-3">
                                        <strong>Status:</strong>
                                        <div>
                                            <span className={`badge bg-${formData.isActive ? 'success' : 'secondary'}`}>
                                                {formData.isActive ? 'Active' : 'Inactive'}
                                            </span>
                                        </div>
                                    </div>
                                    
                                    <div className="mb-3">
                                        <strong>Boundary Points:</strong>
                                        <div className="text-muted">
                                            {coordinates.trim() 
                                                ? `${coordinates.trim().split(' ').length} coordinates`
                                                : <em>No coordinates set</em>
                                            }
                                        </div>
                                    </div>

                                    {formData.description && (
                                        <div className="mb-3">
                                            <strong>Description:</strong>
                                            <div className="text-muted small">
                                                {formData.description}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Submit Button */}
                    <div className="row">
                        <div className="col-12">
                            <div className="d-flex justify-content-end gap-2">
                                <Link
                                    to="/inventory/v1.2/serviceable-areas"
                                    className="btn btn-outline-secondary"
                                >
                                    Cancel
                                </Link>
                                <button
                                    type="submit"
                                    className="btn btn-primary btn-lg"
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? (
                                        <>
                                            <div className="spinner-border spinner-border-sm me-2" role="status">
                                                <span className="visually-hidden">Loading...</span>
                                            </div>
                                            {isNewArea ? 'Creating...' : 'Updating...'}
                                        </>
                                    ) : (
                                        <>
                                            <Save size={16} className="me-2" />
                                            {isNewArea ? 'Create Serviceable Area' : 'Update Serviceable Area'}
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </Fragment>
    );
};

ServiceableArea.propTypes = {
    createServiceableArea: PropTypes.func.isRequired,
    getServiceableArea: PropTypes.func.isRequired,
    updateServiceableArea: PropTypes.func.isRequired,
    clearServiceableArea: PropTypes.func.isRequired,
    serviceableArea: PropTypes.object,
    loading: PropTypes.bool.isRequired
};

const mapStateToProps = (state) => ({
    serviceableArea: state.serviceableArea.serviceableArea,
    loading: state.serviceableArea.loading
});

export default connect(mapStateToProps, {
    createServiceableArea,
    getServiceableArea,
    updateServiceableArea,
    clearServiceableArea
})(ServiceableArea);
